import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithTokenHandling } from '../baseQuery';
import { buildSearchParamsAuto } from '../utils/queryParams';
import {
  COMPANIES_CODE_TYPE,
  COMPANY_USERS_CODE_TYPE,
  COMPANY_MANAGERS_CODE_TYPE,
  COMPANY_ROLES_CODE_TYPE,
  SEARCH_CODE_TYPE,
  DEMO_ENVIRONMENTS_CODE_TYPE,
  ALL_COMPANY_USERS_CODE_TYPE,
} from './constants';
import type {
  CompaniesResponse,
  CompanyUsersResponse,
  CompanyRolesResponse,
  GetAllCompaniesParams,
  CompanyIdParam,
  CompanyUserParams,
  ConvertDemoCompanyParams,
  HubSpotCompanySearchParams,
  HubSpotCompanySearchResponse,
} from './types';

export const companiesApi = createApi({
  reducerPath: 'companies',
  baseQuery: baseQueryWithTokenHandling,
  tagTypes: [
    COMPANIES_CODE_TYPE,
    COMPANY_USERS_CODE_TYPE,
    ALL_COMPANY_USERS_CODE_TYPE,
    COMPANY_MANAGERS_CODE_TYPE,
    COMPANY_ROLES_CODE_TYPE,
    SEARCH_CODE_TYPE,
    DEMO_ENVIRONMENTS_CODE_TYPE,
  ],
  endpoints: (builder) => ({
    getAllCompanies: builder.query<CompaniesResponse, GetAllCompaniesParams | void>({
      query: (params = {}) => {
        const searchParams = buildSearchParamsAuto(params || {});
        const queryString = searchParams.toString();
        return {
          url: `v1/accounts/companies/${queryString ? `?${queryString}` : ''}`,
        };
      },

      providesTags: (result) => {
        const items = result?.results ?? (Array.isArray(result) ? result : []);
        return [
          { type: COMPANIES_CODE_TYPE, id: 'LIST' },
          ...items.map(({ id }: any) => ({
            type: COMPANIES_CODE_TYPE as typeof COMPANIES_CODE_TYPE,
            id,
          })),
        ];
      },
    }),
    getCompanyById: builder.query<MedScout.Company, CompanyIdParam>({
      query: ({ companyId }) => {
        return {
          url: `v1/accounts/companies/${companyId}/`,
        };
      },
      providesTags: (_, __, { companyId }) => [
        { type: COMPANIES_CODE_TYPE, id: companyId },
      ],
    }),

    // Get all company users with recursive pagination
    getAllCompanyUsers: builder.query<MedScout.User[], CompanyIdParam & Record<string, unknown>>({
      async queryFn({ companyId, ...params }, _queryApi, _extraOptions, fetchWithBQ) {
        const searchParams = buildSearchParamsAuto(params);
        const queryString = searchParams.toString();
        const allUsers: any[] = [];

        let url = `v1/accounts/companies/${companyId}/users/${queryString ? `?${queryString}` : ''}`;

        while (url) {
          const result = await fetchWithBQ({ url });

          if (result.error) {
            return { error: result.error };
          }

          const data = result.data as { results: any[]; next: string | null };
          allUsers.push(...data.results);

          if (data.next) {
            // Extract the path from the full URL (remove base URL)
            url = data.next.includes('api/') ? data.next.split('api/')[1] : data.next;
          } else {
            url = '';
          }
        }

        return { data: allUsers };
      },
      providesTags: (result, _, { companyId }) => {
        if (!result) return [{ type: ALL_COMPANY_USERS_CODE_TYPE, id: companyId }];
        return [
          { type: ALL_COMPANY_USERS_CODE_TYPE, id: companyId },
          ...result.map((user: any) => ({
            type: ALL_COMPANY_USERS_CODE_TYPE as typeof ALL_COMPANY_USERS_CODE_TYPE,
            id: `${companyId}-${user.id}`,
          })),
        ];
      },
    }),

    // Company Users endpoints
    getCompanyUsers: builder.query<CompanyUsersResponse, CompanyIdParam & Record<string, unknown>>({
      query: ({ companyId, ...params }) => {
        const searchParams = buildSearchParamsAuto(params);
        const queryString = searchParams.toString();
        return {
          url: `v1/accounts/companies/${companyId}/users/${
            queryString ? `?${queryString}` : ''
          }`,
        };
      },
      providesTags: (result, _, { companyId }) => {
        if (!result?.results)
          return [{ type: COMPANY_USERS_CODE_TYPE, id: companyId }];
        return [
          { type: COMPANY_USERS_CODE_TYPE, id: companyId },
          ...result.results.map((user: any) => ({
            type: COMPANY_USERS_CODE_TYPE as typeof COMPANY_USERS_CODE_TYPE,
            id: `${companyId}-${user.id}`,
          })),
        ];
      },
    }),

    getCompanyUser: builder.query<MedScout.User, CompanyUserParams>({
      query: ({ companyId, userId }) => ({
        url: `v1/accounts/companies/${companyId}/users/${userId}/`,
      }),
      providesTags: (_, __, { companyId, userId }) => [
        { type: COMPANY_USERS_CODE_TYPE, id: `${companyId}-${userId}` },
      ],
    }),

    /**
     * Get company users with infinite scroll pagination
     * Returns 50 users per page, loads more as user scrolls
     */
    getCompanyUsersPaginated: builder.infiniteQuery<
      { results: MedScout.User[]; count: number; next: string | null },
      { companyId: string; q?: string; sort?: string; order?: string },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) =>
          lastPage.next ? lastPageParam + 1 : undefined,
      },
      query({ queryArg, pageParam }) {
        const { companyId, ...params } = queryArg;
        const searchParams = buildSearchParamsAuto({
          ...params,
          page: pageParam,
          pageSize: 50,
        });
        return `v1/accounts/companies/${companyId}/users/?${searchParams.toString()}`;
      },
      providesTags: [COMPANY_USERS_CODE_TYPE],
    }),

    createCompanyUser: builder.mutation<MedScout.User, CompanyIdParam & Record<string, unknown>>({
      query: ({ companyId, ...userData }) => ({
        url: `v1/accounts/companies/${companyId}/users/`,
        method: 'POST',
        body: userData,
      }),
      invalidatesTags: (_, __, { companyId }) => [
        { type: COMPANY_USERS_CODE_TYPE },
        { type: COMPANIES_CODE_TYPE },
        { type: ALL_COMPANY_USERS_CODE_TYPE, id: companyId },
      ],
    }),

    updateCompanyUser: builder.mutation<MedScout.User, CompanyUserParams & Record<string, unknown>>({
      query: ({ companyId, userId, ...userData }) => ({
        url: `v1/accounts/companies/${companyId}/users/${userId}/`,
        method: 'PATCH',
        body: userData,
      }),
      // The company carries the seat figures (`total_active_user_count`, and
      // the active/invited split alongside it), which move whenever a user is
      // deactivated or reactivated — so refresh it here as `createCompanyUser`
      // already does, or the license overview goes stale (MKT-384/MKT-445).
      invalidatesTags: (_, __, { companyId, userId }) => [
        { type: COMPANY_USERS_CODE_TYPE, id: companyId },
        { type: COMPANY_USERS_CODE_TYPE, id: `${companyId}-${userId}` },
        { type: ALL_COMPANY_USERS_CODE_TYPE, id: companyId },
        { type: COMPANIES_CODE_TYPE, id: companyId },
      ],
    }),

    deleteCompanyUser: builder.mutation<void, CompanyUserParams>({
      query: ({ companyId, userId }) => ({
        url: `v1/accounts/companies/${companyId}/users/${userId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, { companyId, userId }) => [
        { type: COMPANY_USERS_CODE_TYPE, id: companyId },
        { type: COMPANY_USERS_CODE_TYPE, id: `${companyId}-${userId}` },
        { type: ALL_COMPANY_USERS_CODE_TYPE, id: companyId },
      ],
    }),

    sendResetPasswordEmail: builder.mutation<void, CompanyUserParams>({
      query: ({ companyId, userId }) => ({
        url: `v1/accounts/companies/${companyId}/users/${userId}/`,
        method: 'PATCH',
        body: { reset_password: true },
      }),
    }),

    sendInviteEmail: builder.mutation<void, CompanyUserParams>({
      query: ({ companyId, userId }) => ({
        url: `v1/accounts/companies/${companyId}/users/${userId}/`,
        method: 'PATCH',
        body: { invite_now: true },
      }),
    }),

    exportCompanyUsers: builder.mutation<Blob, CompanyIdParam & Record<string, unknown>>({
      query: ({ companyId, ...params }) => {
        const searchParams = buildSearchParamsAuto(params);
        const queryString = searchParams.toString();
        return {
          url: `v1/accounts/companies/${companyId}/users/export/${
            queryString ? `?${queryString}` : ''
          }`,
          method: 'GET',
          responseHandler: (response) => response.blob(),
        };
      },
    }),

    // Company Managers endpoints
    getCompanyManagers: builder.query<CompanyUsersResponse, CompanyIdParam>({
      query: ({ companyId }) => ({
        url: `v1/accounts/companies/${companyId}/managers/`,
      }),
      providesTags: (result, _, { companyId }) => {
        if (!result?.results)
          return [{ type: COMPANY_MANAGERS_CODE_TYPE, id: companyId }];
        return [
          { type: COMPANY_MANAGERS_CODE_TYPE, id: companyId },
          ...result.results.map((manager: any) => ({
            type: COMPANY_MANAGERS_CODE_TYPE as typeof COMPANY_MANAGERS_CODE_TYPE,
            id: `${companyId}-${manager.id}`,
          })),
        ];
      },
    }),

    getManagerReports: builder.query<CompanyUsersResponse, CompanyIdParam & { managerId: string } & Record<string, unknown>>({
      query: ({ companyId, managerId, ...params }) => {
        const searchParams = buildSearchParamsAuto(params);
        const queryString = searchParams.toString();
        return {
          url: `v1/accounts/companies/${companyId}/managers/${managerId}/${
            queryString ? `?${queryString}` : ''
          }`,
        };
      },
      providesTags: (_, __, { companyId, managerId }) => [
        {
          type: COMPANY_MANAGERS_CODE_TYPE,
          id: `${companyId}-${managerId}-reports`,
        },
      ],
    }),

    updateManagerReports: builder.mutation<void, CompanyIdParam & { managerId: string } & Record<string, unknown>>({
      query: ({ companyId, managerId, ...data }) => ({
        url: `v1/accounts/companies/${companyId}/managers/${managerId}/`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_, __, { companyId, managerId }) => [
        { type: COMPANY_MANAGERS_CODE_TYPE, id: companyId },
        {
          type: COMPANY_MANAGERS_CODE_TYPE,
          id: `${companyId}-${managerId}-reports`,
        },
      ],
    }),

    deleteManagerReports: builder.mutation<void, { companyId: string; managerId: string }>({
      query: ({ companyId, managerId }) => ({
        url: `v1/accounts/companies/${companyId}/managers/${managerId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, { companyId, managerId }) => [
        { type: COMPANY_MANAGERS_CODE_TYPE, id: companyId },
        {
          type: COMPANY_MANAGERS_CODE_TYPE,
          id: `${companyId}-${managerId}-reports`,
        },
      ],
    }),

    // Company Roles endpoints
    getCompanyRoles: builder.query<CompanyRolesResponse, CompanyIdParam>({
      query: ({ companyId }) => ({
        url: `v1/accounts/companies/${companyId}/roles/`,
      }),
      providesTags: (result, _, { companyId }) => {
        if (!result?.results)
          return [{ type: COMPANY_ROLES_CODE_TYPE, id: companyId }];
        return [
          { type: COMPANY_ROLES_CODE_TYPE, id: companyId },
          ...result.results.map((role: any) => ({
            type: COMPANY_ROLES_CODE_TYPE as typeof COMPANY_ROLES_CODE_TYPE,
            id: `${companyId}-${role.code}`,
          })),
        ];
      },
    }),

    // Create Company endpoint
    createCompany: builder.mutation<MedScout.Company, Record<string, unknown>>({
      query: (data) => ({
        url: 'v1/accounts/companies/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: COMPANIES_CODE_TYPE, id: 'LIST' }],
    }),

    // Update Company endpoint
    updateCompany: builder.mutation<MedScout.Company, CompanyIdParam & Record<string, unknown>>({
      query: ({ companyId, ...data }) => ({
        url: `v1/accounts/companies/${companyId}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_, __, { companyId }) => [
        { type: COMPANIES_CODE_TYPE, id: companyId },
        { type: COMPANIES_CODE_TYPE, id: 'LIST' },
      ],
    }),

    // Update non-MedScout user settings
    updateNonMedScoutUserSettings: builder.mutation<void, CompanyIdParam & Record<string, unknown>>({
      query: ({ companyId, ...data }) => ({
        url: `v1/accounts/companies/${companyId}/non_medscout_user_settings/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_, __, { companyId }) => [
        { type: COMPANIES_CODE_TYPE, id: companyId },
      ],
    }),

    // Delete Company endpoint
    deleteCompany: builder.mutation<void, string>({
      query: (companyId) => ({
        url: `v1/accounts/companies/${companyId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, companyId) => [
        { type: COMPANIES_CODE_TYPE, id: companyId },
        { type: COMPANIES_CODE_TYPE, id: 'LIST' },
      ],
    }),

    // Reactivate Company endpoint
    reactivateCompany: builder.mutation<void, string>({
      query: (companyId) => ({
        url: `v1/accounts/companies/${companyId}/reactivate/`,
        method: 'POST',
      }),
      invalidatesTags: (_, __, companyId) => [
        { type: COMPANIES_CODE_TYPE, id: companyId },
        { type: COMPANIES_CODE_TYPE, id: 'LIST' },
      ],
    }),

    // Create Company endpoint
    createDemoEnv: builder.mutation<void, Record<string, unknown>>({
      query: (data) => ({
        url: 'v1/admin/sales-demo/setup/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: DEMO_ENVIRONMENTS_CODE_TYPE, id: 'DEMO' }],
    }),

    // Convert Demo Company to Customer
    convertDemoCompany: builder.mutation<void, ConvertDemoCompanyParams>({
      query: ({ companyId, ...body }) => ({
        url: `v1/admin/sales-demo/${companyId}/convert/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, __, { companyId }) => [
        { type: COMPANIES_CODE_TYPE, id: companyId },
        { type: COMPANIES_CODE_TYPE, id: 'LIST' },
        { type: COMPANY_USERS_CODE_TYPE },
        { type: DEMO_ENVIRONMENTS_CODE_TYPE },
      ],
    }),

    // Search Companies and Users endpoint
    searchCompaniesAndUsers: builder.query<Record<string, any>[], string>({
      query: (searchQuery: string) => ({
        url: `v1/accounts/company_and_user/search/?q=${searchQuery}`,
      }),
      providesTags: [SEARCH_CODE_TYPE],
    }),

    // HubSpot company typeahead for linking a company to its HubSpot record (MKT-675)
    searchHubSpotCompanies: builder.query<
      HubSpotCompanySearchResponse,
      HubSpotCompanySearchParams
    >({
      query: (params) => {
        const searchParams = buildSearchParamsAuto(params);
        return {
          url: `v1/admin/hubspot/companies/?${searchParams.toString()}`,
        };
      },
    }),
  }),
});

export const {
  useGetAllCompaniesQuery,
  useGetCompanyByIdQuery,
  useGetAllCompanyUsersQuery,
  useGetCompanyUsersQuery,
  useGetCompanyUserQuery,
  useGetCompanyUsersPaginatedInfiniteQuery,
  useCreateCompanyUserMutation,
  useUpdateCompanyUserMutation,
  useDeleteCompanyUserMutation,
  useSendResetPasswordEmailMutation,
  useSendInviteEmailMutation,
  useExportCompanyUsersMutation,
  useGetCompanyManagersQuery,
  useGetManagerReportsQuery,
  useUpdateManagerReportsMutation,
  useDeleteManagerReportsMutation,
  useGetCompanyRolesQuery,
  useCreateCompanyMutation,
  useUpdateCompanyMutation,
  useUpdateNonMedScoutUserSettingsMutation,
  useDeleteCompanyMutation,
  useReactivateCompanyMutation,
  useSearchCompaniesAndUsersQuery,
  useSearchHubSpotCompaniesQuery,
  useCreateDemoEnvMutation,
  useConvertDemoCompanyMutation,
} = companiesApi;
